import * as React from 'react';
import { atom, useRecoilState } from 'recoil';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    Collection,
    DialogRoot,
    DialogTrigger,
    Disclosure,
    DisclosureChevron,
    DisclosurePanel,
    DisclosureTrigger,
    HeadlessTreeItemContent,
    type Key,
    SidebarActionButton,
    SidebarEmptyState,
    SidebarItemIcon,
    SidebarSection,
    SidebarSectionHeading,
    SidebarSectionHeadingActions,
    SidebarSectionHeadingButton,
    SidebarTree,
    SidebarTreeItem,
    SidebarTreeItemExpandButton,
    SidebarTreeItemText,
} from '@gitbook/spine-react';

import { CreateSiteDialog, getSitePaletteFactory } from '../../Components';
import { useNavigation } from '../../navigation';
import { type AnySiteItem, getSiteTreeItem } from '../../state';
import { persistRecoilEffect } from '../../utils';
import { SidebarActionsButton } from '../SidebarActionsButton';

const disclosureExpandedAtom = atom<boolean>({
    key: 'sidebarSitesDisclosureExpanded',
    default: true,
    effects: [persistRecoilEffect()],
});

export function SitesDisclosure(props: {
    organization: api.Organization;
    content: api.OrganizationAllContent;
    screen: NavigationScreenState;
}) {
    const {
        organization,
        content: { sites },
        screen,
    } = props;
    const [isExpanded, setExpanded] = useRecoilState(disclosureExpandedAtom);
    return (
        <Disclosure isExpanded={isExpanded} onExpandedChange={setExpanded}>
            <SidebarSection>
                <SitesSectionHeading organization={organization} />
                <DisclosurePanel>
                    {sites.length === 0 ? (
                        <SidebarEmptyState>
                            {organization.permissions.createContent
                                ? 'Create your first site and publish your content to the web.'
                                : 'There are no sites in your organization yet.'}
                        </SidebarEmptyState>
                    ) : (
                        <SitesTree sites={sites} screen={screen} organizationId={organization.id} />
                    )}
                </DisclosurePanel>
            </SidebarSection>
        </Disclosure>
    );
}

const SitesSectionHeading = React.memo(function SitesSectionHeading(props: {
    organization: api.Organization;
}) {
    const { organization } = props;
    return (
        <SidebarSectionHeading>
            <DisclosureTrigger>
                <SidebarSectionHeadingButton>
                    Docs sites
                    <DisclosureChevron />
                    {organization.permissions.createContent && (
                        <SidebarSectionHeadingActions>
                            <DialogRoot>
                                <DialogTrigger>
                                    <SidebarActionButton label="Create a site" icon={Icon.Plus} />
                                </DialogTrigger>

                                <CreateSiteDialog organizationId={organization.id} />
                            </DialogRoot>
                        </SidebarSectionHeadingActions>
                    )}
                </SidebarSectionHeadingButton>
            </DisclosureTrigger>
        </SidebarSectionHeading>
    );
});

/**
 * Represent the expanded state of the sites in the sidebar.
 */
const expandedSiteItemsAtom = atom<Set<Key>>({
    key: 'sidebarSitesExpanded',
    default: new Set<Key>(),
    effects: [
        persistRecoilEffect({
            serializer: {
                serialize: (value) => JSON.stringify(Array.from(value)),
                deserialize: (value) => new Set(JSON.parse(value)),
            },
        }),
    ],
});

/**
 * Represent the sites that have been touched by the user.
 */
const touchedSitesAtom = atom<Set<Key>>({
    key: 'sidebarSitesTouched',
    default: new Set<Key>(),
    effects: [
        persistRecoilEffect({
            serializer: {
                serialize: (value) => JSON.stringify(Array.from(value)),
                deserialize: (value) => new Set(JSON.parse(value)),
            },
        }),
    ],
});

/**
 * Hook to manage the expanded state of the sites in the sidebar.
 * If a site is not touched yet, it will be expanded by default.
 */
function useSiteItemsExpandedState(props: { siteKeys: Set<Key> }) {
    const { siteKeys } = props;
    const [expandedKeys, setExpandedKeys] = useRecoilState(expandedSiteItemsAtom);
    const [touchedKeys, setTouchedKeys] = useRecoilState(touchedSitesAtom);
    const onExpandedChange = React.useCallback(
        (keys: Set<Key>) => {
            setTouchedKeys((prev) => {
                const newTouched = new Set(prev);
                for (const key of siteKeys) {
                    if (!keys.has(key)) {
                        newTouched.add(key);
                    }
                }
                return newTouched.size !== prev.size ? newTouched : prev;
            });
            setExpandedKeys(keys);
        },
        [setExpandedKeys, setTouchedKeys, siteKeys]
    );
    const computedExpandedKeys = React.useMemo(() => {
        const expanded = new Set(expandedKeys);
        // Mark all sites as expanded if they are not touched yet.
        for (const siteKey of siteKeys) {
            if (!expanded.has(siteKey) && !touchedKeys.has(siteKey)) {
                expanded.add(siteKey);
            }
        }
        return expanded;
    }, [expandedKeys, siteKeys, touchedKeys]);
    return { expandedKeys: computedExpandedKeys, onExpandedChange };
}

function SitesTree(props: {
    sites: api.OrganizationAllSite[];
    screen: NavigationScreenState;
    organizationId: string;
}) {
    const { sites, screen, organizationId } = props;
    const items = React.useMemo(
        () => sites.map((site) => getSiteTreeItem(site, { hideEmptySectionGroups: true })),
        [sites]
    );
    const siteKeys = React.useMemo<Set<Key>>(() => new Set(items.map((item) => item.id)), [items]);
    const expandedState = useSiteItemsExpandedState({ siteKeys });
    return (
        <SidebarTree<AnySiteItem>
            // Temporary workaround for a bug when switching between orgs
            key={organizationId}
            dependencies={[screen]}
            items={items}
            aria-label="Docs sites"
            {...expandedState}
        >
            {function renderItem(treeItem) {
                const isExpandable = 'children' in treeItem && treeItem.children.length > 0;
                const href = 'href' in treeItem ? treeItem.href : undefined;
                const isActive = 'isActive' in treeItem ? treeItem.isActive : undefined;

                return (
                    <SidebarTreeItem href={href} isActive={isActive?.(screen)} {...props}>
                        <SiteSidebarTreeItemContent
                            value={treeItem}
                            isExpandable={isExpandable}
                            organizationId={organizationId}
                        />
                        {'children' in treeItem ? (
                            <Collection items={treeItem.children}>{renderItem}</Collection>
                        ) : null}
                    </SidebarTreeItem>
                );
            }}
        </SidebarTree>
    );
}

const SiteSidebarTreeItemContent = React.memo(function SiteSidebarTreeItemContent(props: {
    value: AnySiteItem;
    isExpandable: boolean;
    organizationId: string;
}) {
    const { value, isExpandable, organizationId } = props;
    return (
        <HeadlessTreeItemContent>
            {isExpandable ? (
                <SidebarTreeItemExpandButton icon={value.icon} />
            ) : (
                <SidebarItemIcon icon={value.icon} />
            )}
            <SidebarTreeItemText>{value.title}</SidebarTreeItemText>
            {value.type === 'site' ? (
                <SiteSidebarTreeActions site={value.site} organizationId={organizationId} />
            ) : null}
        </HeadlessTreeItemContent>
    );
});

function SiteSidebarTreeActions(props: { site: api.Site; organizationId: string }) {
    const { site, organizationId } = props;
    const { navigate } = useNavigation();
    const palette = getSitePaletteFactory({ site, navigate, organizationId });
    if (!palette) {
        return null;
    }
    return <SidebarActionsButton palette={palette} />;
}
